import Response from '../modules/classes/Response';
import Company from '../models/Company';

class CompanyController {
    constructor(apiService) {
        this.apiService = apiService;
    }
 
    async createCompany(data) {
        try {
            const response = await this.apiService.requestWithFormData('/company', 'POST', data);
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to create company');
        }
    }

    /**
     * Upload company avatar
     * @param {string} companyId - Company ID
     * @param {Object} imageData - Image data (uri, type, fileName)
     * @returns {Promise<Response>} - Response containing updated company
     */
    async uploadCompanyAvatar(companyId, imageData) {
        try {
            const formData = new FormData();
            
            if (typeof imageData === 'string') {
                // If it's a string URI
                const uri = imageData;
                const name = uri.split('/').pop();
                const match = /\.(\w+)$/.exec(name);
                const type = match ? `image/${match[1]}` : 'image/jpeg';
                
                formData.append('avatar', {
                    uri,
                    name,
                    type
                });
            } else {
                // Assume it's already a proper object
                formData.append('avatar', imageData);
            }
            
            const response = await this.apiService.put(
                `/company/${companyId}`, 
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            
            const companyData = response.data;
            
            if (companyData) {
                const company = Company.fromAPI(companyData);
                
                return new Response({
                    success: true,
                    data: company,
                    message: 'Company avatar updated successfully',
                });
            }
            
            return new Response({
                success: false,
                message: 'Failed to update company avatar',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to update company avatar',
                error,
            });
        }
    }

    /**
     * Upload company banner
     * @param {string} companyId - Company ID
     * @param {Object} imageData - Image data (uri, type, fileName)
     * @returns {Promise<Response>} - Response containing updated company
     */
    async uploadCompanyBanner(companyId, imageData) {
        try {
            const formData = new FormData();
            
            if (typeof imageData === 'string') {
                // If it's a string URI
                const uri = imageData;
                const name = uri.split('/').pop();
                const match = /\.(\w+)$/.exec(name);
                const type = match ? `image/${match[1]}` : 'image/jpeg';
                
                formData.append('banner', {
                    uri,
                    name,
                    type
                });
            } else {
                // Assume it's already a proper object
                formData.append('banner', imageData);
            }
            
            const response = await this.apiService.put(
                `/company/${companyId}`, 
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            
            const companyData = response.data;
            
            if (companyData) {
                const company = Company.fromAPI(companyData);
                
                return new Response({
                    success: true,
                    data: company,
                    message: 'Company banner updated successfully',
                });
            }
            
            return new Response({
                success: false,
                message: 'Failed to update company banner',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to update company banner',
                error,
            });
        }
    }

    /**
     * Get all companies
     * @param {Object} filters - Filter options
     * @returns {Promise<Response>} - Response containing array of companies
     */
    async getCompanies(filters = {}) {
        try {
            const body = await this.apiService.get('/company', { params: filters });
            return Response.parse({
                statusCode: 200,
                message: 'Companies fetched successfully',
                data: body,
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch companies',
                error,
            });
        }
    }

    /**
     * Get pending companies
     * @returns {Promise<Response>} - Response containing array of pending companies
     */
    async getPendingCompaniesAsAdmin(filters = { skip: 0, take: 10 }) {
        try {
            const response = await this.apiService.requestWithParams('/admin/company/drafts/pending', 'GET', filters);
            return Response.parse(response);
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch pending companies',
                error,
            });
        }
    }

    async getCompanyDraftsByStatus(status, filters = {}) {
        try {
            const response = await this.apiService.requestWithParams(`/admin/company/drafts/${status}`, 'GET', filters);
            return Response.parse(response);
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch company drafts',
                error,
            });
        }
    }

    async getPendingCompanies() {
        try {
            const body = await this.apiService.get('/company/status/pending-rejects');
            return Response.parse({
                statusCode: 200,
                message: 'Pending companies fetched successfully',
                data: body,
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch pending companies',
                error,
            });
        }
    }

    /**
     * Get company by ID
     * @param {string} companyId - Company ID
     * @returns {Promise<Response>} - Response containing company data
     */
    async getCompanyById(companyId) {
        try {
            const body = await this.apiService.get(`/company/${companyId}`);
            const companyData = body;
            
            if (companyData) {
                return new Response({
                    success: true,
                    data: companyData,
                    message: 'Company fetched successfully',
                });
            }
            
            return new Response({
                success: false,
                message: 'Failed to fetch company',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch company',
                error,
            });
        }
    }

    /**
     * Update company
     * @param {string} companyId - Company ID
     * @param {Object} updateData - Data to update
     * @returns {Promise<Response>} - Response containing updated company
     */
    async updateCompany(companyId, updateData) {
        try {
            const response = await this.apiService.put(`/company/${companyId}`, updateData);
            const companyData = response.data;
            
            if (companyData) {
                const company = Company.fromAPI(companyData);
                
                return new Response({
                    success: true,
                    data: company,
                    message: 'Company updated successfully',
                });
            }
            
            return new Response({
                success: false,
                message: 'Failed to update company',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to update company',
                error,
            });
        }
    }

    /**
     * Approve company
     * @param {string} companyId - Company ID
     * @param {Object} approvalData - Approval data (approvedBy, approvedByRole, etc.)
     * @returns {Promise<Response>} - Response containing approved company
     */
    async approveCompany(companyId, approvalData = {}) {
        try {
            const response = await this.apiService.patch(`/admin/company/${companyId}/approve`, approvalData);
            return Response.parse(response);
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to approve company',
                error,
            });
        }
    }

    /**
     * Reject company
     * @param {string} companyId - Company ID
     * @param {Object} rejectionData - Rejection data (rejectionReason, etc.)
     * @returns {Promise<Response>} - Response containing rejected company
     */
    async rejectCompany(companyId, rejectionData = {}) {
        try {
            const response = await this.apiService.patch(`/admin/company/${companyId}/reject`, rejectionData);
            return Response.parse(response);
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to reject company',
                error,
            });
        }
    }

    /**
     * Delete company
     * @param {string} companyId - Company ID
     * @returns {Promise<Response>} - Response containing deletion status
     */
    async deleteCompany(companyId) {
        try {
            await this.apiService.delete(`/company/${companyId}`);
            
            return new Response({
                success: true,
                message: 'Company deleted successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to delete company',
                error,
            });
        }
    }

    /**
     * Get trusted stores (distributors)
     * @param {Object} params - Query parameters (skip, take, preview)
     * @returns {Promise<Response>} - Response containing array of trusted stores
     */
    async getTrustedStores(params = { preview: true }) {
        try {
            const response = await this.apiService.get('/company/trusted-stores', { params });
            return {
                success: true,
                data: response.data?.data || response.data || [],
                message: 'Trusted stores fetched successfully',
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                message: error.message || 'Failed to fetch trusted stores',
                error,
            };
        }
    }

    /**
     * Get manufacturers
     * @param {Object} params - Query parameters (skip, take, preview)
     * @returns {Promise<Response>} - Response containing array of manufacturers
     */
    async getManufacturers(params = { preview: true }) {
        try {
            const response = await this.apiService.get('/company/manufacturers', { params });
            return {
                success: true,
                data: response.data?.data || response.data || [],
                message: 'Manufacturers fetched successfully',
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                message: error.message || 'Failed to fetch manufacturers',
                error,
            };
        }
    }

    /**
     * Get brands
     * @param {Object} params - Query parameters (skip, take, preview)
     * @returns {Promise<Response>} - Response containing array of brands
     */
    async getBrands(params = { preview: true }) {
        try {
            const response = await this.apiService.get('/company/brands', { params });
            return {
                success: true,
                data: response.data?.data || response.data || [],
                message: 'Brands fetched successfully',
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                message: error.message || 'Failed to fetch brands',
                error,
            };
        }
    }

    /**
     * Get company team members
     * @param {string} companyId - Company ID
     * @returns {Promise<Response>} - Response containing team members
     */
    async getCompanyTeam(companyId) {
        try {
            const response = await this.apiService.get(`/company/${companyId}/team`);
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to load company team', error.status || 500);
        }
    }

    /**
     * Get pending company invitations for the current user
     * @returns {Promise<Response>}
     */
    async getPendingCompanyInvitations() {
        try {
            const response = await this.apiService.get('/company/team/invitations');
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to load invitations', error.status || 500);
        }
    }

    /**
     * Accept a company invitation
     * @param {string} companyId - Company ID
     * @returns {Promise<Response>}
     */
    async acceptCompanyInvitation(companyId) {
        try {
            const response = await this.apiService.post(`/company/${companyId}/team/invite/accept`, {});
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to accept invitation', error.status || 500);
        }
    }

    /**
     * Decline a company invitation
     * @param {string} companyId - Company ID
     * @returns {Promise<Response>}
     */
    async declineCompanyInvitation(companyId) {
        try {
            const response = await this.apiService.post(`/company/${companyId}/team/invite/decline`, {});
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to decline invitation', error.status || 500);
        }
    }

    /**
     * Invite/add a team member by username
     * @param {string} companyId - Company ID
     * @param {{ username: string, role: string }} data - Invite payload
     * @returns {Promise<Response>}
     */
    async inviteTeamMember(companyId, data) {
        try {
            const response = await this.apiService.post(`/company/${companyId}/team/invite`, data);
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to invite team member', error.status || 500);
        }
    }

    /**
     * Update a team member's role
     * @param {string} companyId - Company ID
     * @param {string} userId - Target user ID
     * @param {{ role: string }} data - Role payload
     * @returns {Promise<Response>}
     */
    async updateTeamMemberRole(companyId, userId, data) {
        try {
            const response = await this.apiService.put(`/company/${companyId}/team/${userId}/role`, data);
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to update team member role', error.status || 500);
        }
    }

    /**
     * Remove a team member
     * @param {string} companyId - Company ID
     * @param {string} userId - Target user ID
     * @returns {Promise<Response>}
     */
    async removeTeamMember(companyId, userId) {
        try {
            const response = await this.apiService.delete(`/company/${companyId}/team/${userId}`);
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to remove team member', error.status || 500);
        }
    }

    /**
     * Get company's owned shelf collection (releases)
     * @param {string} companyId - Company ID
     * @param {number} page - Page number (1-indexed)
     * @param {number} pageSize - Items per page
     * @returns {Promise<Response>} - Response containing paginated shelf data
     */
    async getCompanyShelf(companyId, page = 0, pageSize = 20) {
        try {
            console.log(companyId, page, pageSize);
            const response = await this.apiService.get(`/company/${companyId}/shelf`, {
                params: { page, pageSize }
            });
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to fetch company shelf');
        }
    }

    async getCompanyFigures(companyId, skip = 0, take = 20) {
        try {
            const response = await this.apiService.get(`/company/${companyId}/figures`, {
                params: { skip, take }
            });

            console.log("FIGURES: ", response)
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to fetch company figures');
        }
    }

    async getUserSubmittedFigures(companyId, skip = 0, take = 20) {
        try {
            const response = await this.apiService.get(`/company/${companyId}/user-figures`, {
                params: { skip, take }
            });
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to fetch user-submitted figures');
        }
    }

    async getUnreleasedCompanyFigures(companyId, skip = 0, take = 20) {
        try {
            const response = await this.apiService.get(`/company/${companyId}/unreleased-figures`, {
                params: { skip, take }
            });
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to fetch unreleased figures');
        }
    }

    async releaseCompanyFigure(companyId, figureId, postToFeed = false, caption = '') {
        try {
            const response = await this.apiService.post(`/company/${companyId}/figures/${figureId}/release`, {
                postToFeed,
                caption
            });
            return Response.parse(response);
        } catch (error) {
            return Response.error(error.message || 'Failed to release figure');
        }
    }

    /**
     * Create a new figure for a company
     * @param {string} companyId - Company ID
     * @param {FormData} formData - Figure data as FormData
     * @returns {Promise<Response>} - Response containing the created figure
     */
    async createCompanyFigure(companyId, formData) {
        try {
            const response = await this.apiService.post(
                `/company/${companyId}/figures`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            
            return new Response({
                success: true,
                data: response.data,
                message: 'Figure created successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to create figure',
                error,
            });
        }
    }

    /**
     * Track a company page view
     * @param {string} companyId - Company ID
     * @returns {Promise<Response>} - Response indicating if view was tracked
     */
    async trackCompanyView(companyId) {
        try {
            const response = await this.apiService.post(`/company/${companyId}/track-view`);
            return new Response({
                success: true,
                data: response.data?.data || response.data,
                message: 'Company view tracked',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to track company view',
                error,
            });
        }
    }
}

export default CompanyController;